import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStore, faLocationDot, faStar, faSearch, faPlus, faSpinner, faGlobe } from '@fortawesome/free-solid-svg-icons';
import { cn } from '../../../../ui/utils/cn';
import { Alert, AlertDescription, Input } from '../../../../ui';

interface NearbyCoffeeShop {
    _id?: string;
    name: string;
    place_id?: string;
    status?: string;
    coordinates: {
        lat: number;
        lng: number;
    };
    distance: number;
    address: string;
    rating?: number;
    price_level?: number;
    types?: string[];
    ratingStats?: {
        totalRatings: number;
        averageRating: number;
        coffeeTypeStats?: Record<string, { count: number; averageRating: number }>;
    };
}

interface CoffeeShopListProps {
    shops: NearbyCoffeeShop[];
    isLoading: boolean;
    hasLocation: boolean;
    selectedPlaceId?: string;
    onSelect: (shop: NearbyCoffeeShop) => void;
    showAdminStats?: boolean;
    isAdmin?: boolean;
    maxShops?: number;
    onAddNewShop?: () => void;
    searchValue?: string;
    onSearchChange?: (value: string) => void;
    clusterStatus?: 'none' | 'initializing' | 'ready';
    // inline Google-by-name search (skips the add-new dialog)
    onGoogleSearch?: (query: string) => void;
    googleResults?: NearbyCoffeeShop[];
    isSearchingGoogle?: boolean;
    googleSearched?: boolean;
    onSelectGoogleResult?: (shop: NearbyCoffeeShop) => void;
    proposingPlaceId?: string;
}

// loading skeleton component
const ShopSkeleton: React.FC = () => (
    <div className="p-3 border border-ink/10 bg-muted/20 rounded-lg animate-pulse">
        <div className="flex items-start justify-between gap-2">
            <div className="flex-1 space-y-2">
                <div className="h-4 bg-muted rounded-md w-3/4"></div>
                <div className="h-3 bg-muted rounded-md w-1/2"></div>
            </div>
        </div>
    </div>
);

// format distance for display
const formatDistance = (meters: number): string => {
    if (meters >= 1000) {
        return `${(meters / 1000).toFixed(1)} km`;
    }
    return `${meters} m`;
};

const CoffeeShopList: React.FC<CoffeeShopListProps> = ({
    shops,
    isLoading,
    hasLocation,
    selectedPlaceId,
    onSelect,
    showAdminStats = false,
    isAdmin = false,
    maxShops = 30,
    onAddNewShop,
    searchValue = '',
    onSearchChange,
    clusterStatus,
    onGoogleSearch,
    googleResults = [],
    isSearchingGoogle = false,
    googleSearched = false,
    onSelectGoogleResult,
    proposingPlaceId
}) => {
    const trimmedSearch = searchValue.trim();

    // reusable Google-by-name search block: the typed filter doubles as a
    // Google query so users never have to retype in the add-new dialog
    const renderGoogleSearch = () => {
        if (!onGoogleSearch || !trimmedSearch) return null;
        return (
            <div className="space-y-2">
                <button
                    type="button"
                    onClick={() => onGoogleSearch(trimmedSearch)}
                    disabled={isSearchingGoogle}
                    className="w-full p-2.5 border border-sky/40 bg-sky/5 rounded-lg cursor-pointer transition-all hover:bg-sky/10 disabled:opacity-60 flex items-center justify-center gap-2"
                >
                    <FontAwesomeIcon
                        icon={isSearchingGoogle ? faSpinner : faGlobe}
                        className={cn('h-3.5 w-3.5 text-sky', isSearchingGoogle && 'animate-spin')}
                    />
                    <span className="text-sm font-medium text-sky">
                        {isSearchingGoogle ? 'Searching Google…' : `Search "${trimmedSearch}" on Google`}
                    </span>
                </button>

                {googleSearched && !isSearchingGoogle && googleResults.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-1">
                        No Google results for "{trimmedSearch}"
                    </p>
                )}

                {googleResults.length > 0 && (
                    <div className="space-y-2 border border-sky/20 bg-sky/5 rounded-xl p-3">
                        <div className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                            <FontAwesomeIcon icon={faGlobe} className="h-3 w-3 text-sky" />
                            From Google
                        </div>
                        {googleResults.map((shop, index) => {
                            const isProposing = !!shop.place_id && proposingPlaceId === shop.place_id;
                            return (
                                <button
                                    key={shop.place_id || index}
                                    type="button"
                                    onClick={() => onSelectGoogleResult?.(shop)}
                                    disabled={!!proposingPlaceId}
                                    className="w-full text-left p-3 border border-ink/10 bg-background rounded-lg cursor-pointer transition-all hover:shadow-md hover:border-mint/40 hover:bg-mint/5 disabled:opacity-60"
                                >
                                    <div className="font-medium text-sm truncate flex items-center gap-2">
                                        {shop.name || 'Unknown Shop'}
                                        {isProposing && (
                                            <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin text-mint" />
                                        )}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-muted-foreground">
                                        {shop.distance > 0 && (
                                            <span className="flex items-center gap-1">
                                                <FontAwesomeIcon icon={faLocationDot} className="h-2.5 w-2.5" />
                                                {formatDistance(shop.distance)}
                                            </span>
                                        )}
                                        {shop.rating && (
                                            <span className="flex items-center gap-1">
                                                <FontAwesomeIcon icon={faStar} className="h-2.5 w-2.5 text-gold" />
                                                {shop.rating.toFixed(1)}
                                            </span>
                                        )}
                                        {shop.price_level && (
                                            <span>{'€'.repeat(shop.price_level)}</span>
                                        )}
                                    </div>
                                    {shop.address && (
                                        <div className="text-xs text-muted-foreground mt-1 truncate">
                                            {shop.address}
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    // initializing area — first coffee in this cluster, backend is fetching nearby
    if (clusterStatus === 'initializing' && shops.length === 0) {
        return (
            <div className="space-y-3">
                <div className="p-6 border border-sky/30 bg-sky/5 rounded-xl text-center space-y-3">
                    <FontAwesomeIcon icon={faSearch} className="h-8 w-8 text-sky animate-spin" />
                    <div>
                        <div className="font-medium text-sm">You're the first coffee in this area</div>
                        <div className="text-xs text-muted-foreground mt-1">
                            We're fetching nearby coffee shops…
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // loading state with skeleton
    if (isLoading) {
        return (
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold flex items-center gap-2">
                        <FontAwesomeIcon icon={faSearch} className="h-3 w-3 animate-spin" />
                        Searching nearby...
                    </h4>
                </div>
                <div className="max-h-[350px] overflow-y-auto space-y-2 border border-ink/10 bg-muted/10 rounded-xl p-3">
                    {[1, 2, 3, 4].map((i) => (
                        <ShopSkeleton key={i} />
                    ))}
                </div>
            </div>
        );
    }

    // no location state
    if (!hasLocation) {
        return (
            <Alert variant="warning">
                <AlertDescription className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faLocationDot} className="h-4 w-4" />
                    Get your location first to see nearby coffee shops
                </AlertDescription>
            </Alert>
        );
    }

    // empty state
    if (shops.length === 0) {
        return (
            <div className="space-y-3">
                <div className="p-6 border border-ink/10 bg-muted/20 rounded-xl text-center space-y-3">
                    <FontAwesomeIcon icon={faStore} className="h-8 w-8 text-muted-foreground" />
                    <div>
                        <div className="font-medium text-sm">No coffee shops found nearby</div>
                        <div className="text-xs text-muted-foreground mt-1">
                            Type a shop name to search Google, or add a new shop below
                        </div>
                    </div>
                </div>
                {onSearchChange && (
                    <Input
                        placeholder="Search by shop name…"
                        value={searchValue}
                        onChange={(e) => onSearchChange(e.target.value)}
                        className="h-8 text-sm"
                    />
                )}
                {renderGoogleSearch()}
                {onAddNewShop && (
                    <button
                        type="button"
                        onClick={onAddNewShop}
                        className="w-full p-3 border border-dashed border-ink/20 bg-muted/10 rounded-lg cursor-pointer transition-all hover:border-mint/40 hover:bg-mint/5 flex items-center justify-center gap-2"
                    >
                        <FontAwesomeIcon icon={faPlus} className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium text-muted-foreground">Add new coffee shop</span>
                    </button>
                )}
            </div>
        );
    }

    const filteredShops = searchValue.trim()
        ? shops.filter(s =>
            s.name.toLowerCase().includes(searchValue.toLowerCase()) ||
            (s.address || '').toLowerCase().includes(searchValue.toLowerCase())
        )
        : shops;
    const displayShops = filteredShops.slice(0, maxShops);

    return (
        <div className="space-y-3">
            {/* header with count and optional search */}
            <div className="flex items-center justify-between gap-2">
                <h4 className="text-sm font-semibold flex items-center gap-2">
                    <FontAwesomeIcon icon={faStore} className="h-3 w-3 text-muted-foreground" />
                    Nearby Coffee Shops
                </h4>
                <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-sm">
                    {filteredShops.length} found
                </span>
            </div>
            {onSearchChange && (
                <Input
                    placeholder="Filter by name or address..."
                    value={searchValue}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className="h-8 text-sm"
                />
            )}

            {/* shops list (scrollable) */}
            {displayShops.length > 0 && (
            <div className="max-h-[350px] overflow-y-auto space-y-2 border border-ink/10 bg-muted/10 rounded-xl p-3">
                {displayShops.map((shop, index) => {
                    const shopId = shop.place_id || shop._id;
                    const isSelected = selectedPlaceId === shopId;
                    
                    return (
                        <div
                            key={shopId || index}
                            className={cn(
                                "p-3 border border-ink/10 bg-background rounded-lg cursor-pointer transition-all",
                                "hover:shadow-md hover:border-mint/40 hover:bg-mint/5",
                                isSelected && "bg-mint/10 border-mint/50 shadow-sm"
                            )}
                            onClick={() => onSelect(shop)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    onSelect(shop);
                                }
                            }}
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium text-sm truncate flex items-center gap-2">
                                        {shop.name || 'Unknown Shop'}
                                        {isSelected && (
                                            <span className="text-xs text-mint">✓</span>
                                        )}
                                        {shop.status === 'pending_review' && (
                                            <span className="text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">pending</span>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-muted-foreground">
                                        <span className="flex items-center gap-1">
                                            <FontAwesomeIcon icon={faLocationDot} className="h-2.5 w-2.5" />
                                            {formatDistance(shop.distance)}
                                        </span>
                                        {shop.rating && (
                                            <span className="flex items-center gap-1">
                                                <FontAwesomeIcon icon={faStar} className="h-2.5 w-2.5 text-gold" />
                                                {shop.rating.toFixed(1)}
                                            </span>
                                        )}
                                        {shop.price_level && (
                                            <span>{'€'.repeat(shop.price_level)}</span>
                                        )}
                                    </div>
                                    {shop.address && (
                                        <div className="text-xs text-muted-foreground mt-1 truncate">
                                            {shop.address}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* admin stats */}
                            {showAdminStats && isAdmin && shop.ratingStats && shop.ratingStats.totalRatings > 0 && (
                                <div className="mt-2 pt-2 border-t border-ink/20 text-xs text-muted-foreground">
                                    <div className="flex items-center gap-2">
                                        <span>☕ {shop.ratingStats.totalRatings} rated</span>
                                        <span>•</span>
                                        <span>Avg: {shop.ratingStats.averageRating.toFixed(1)}/10</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
                
                {/* show more indicator */}
                {filteredShops.length > maxShops && (
                    <div className="text-center text-xs text-muted-foreground py-2">
                        + {filteredShops.length - maxShops} more shops available
                    </div>
                )}
            </div>
            )}

            {/* no local match — nudge toward the Google search */}
            {trimmedSearch && filteredShops.length === 0 && (
                <p className="text-xs text-muted-foreground text-center">
                    No nearby shop matches "{trimmedSearch}"
                </p>
            )}

            {/* inline Google-by-name search (uses the filter text as the query) */}
            {renderGoogleSearch()}

            {/* add new shop - always visible below list so user can add when their shop isn't there */}
            {onAddNewShop && (
                <button
                    type="button"
                    onClick={onAddNewShop}
                    className="w-full p-3 border border-dashed border-ink/20 bg-muted/10 rounded-lg cursor-pointer transition-all hover:border-mint/40 hover:bg-mint/5 flex items-center justify-center gap-2"
                >
                    <FontAwesomeIcon icon={faPlus} className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-muted-foreground">Add new coffee shop</span>
                </button>
            )}
        </div>
    );
};

export default CoffeeShopList;
